import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';
import { Avatar, Box, Button, Chip, Divider, Switch, Typography } from '@mui/material';
import { Business_Profile_Enum, Global_Product_Family_Enum, Product_Family_Name_Enum, User_Role_Enum } from '@kheops/graphql';
import { ProductFamilyMap, productFamilyMap } from '@kheops/utils';
import Sheet from '../../common/components/Sheet';
import { ProductFamilyTypeIntl, PackagingFamilyTypeIntl } from '../../common/i18n/product-family.translation';
import PhotosUtils from '../../common/utils/photos.utils';
import ProductFamilyChip from '../../user/settingsDialog/ProductFamilyChip';
import { currentContextAtom } from '../../state';
import { ellipsisStyle } from '../../common/utils/style.utils';
import { IntercomArticlesIds } from '../../common/utils/intercom.articles.enum';
import { useCompanyTeamRoleWording } from './useCompanyTeamRoleWording';
import { BUYER_ROLES, SUPPLIER_ROLES, UserRowData } from './CompanyTeamPage';

export interface CompanyTeamUserDetailProps {
  open: boolean;
  close: (newRole?: User_Role_Enum, isAdmin?: boolean) => void;
  loading: boolean;
  data: UserRowData;
}

export default function CompanyTeamUserDetail({ open, close, loading, data: { user, role, isAdmin, productFamilies } }: CompanyTeamUserDetailProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'company', 'settings']);
  const { realm, companyBrand } = useAtomValue(currentContextAtom);
  const [currentRole, setCurrentRole] = useState(role);
  const [currentAdminStatus, setCurrentAdminStatus] = useState(isAdmin);
  const resolvedWording = useCompanyTeamRoleWording(companyBrand || undefined);
  const availableRoles = realm === Business_Profile_Enum.Buyer ? BUYER_ROLES : SUPPLIER_ROLES;

  const roleUpdated = isAdmin !== currentAdminStatus || role !== currentRole;

  const subFamiliesByFamilies = useMemo(() => {
    if (productFamilies[0]?.global_setting) {
      return;
    }

    const result = {
      [Product_Family_Name_Enum.Beverages]: [],
      [Product_Family_Name_Enum.FreshFood]: [],
      [Product_Family_Name_Enum.Grocery]: [],
      [Product_Family_Name_Enum.NonFoodProducts]: [],
    } as ProductFamilyMap;

    productFamilies.forEach(({ product_family, product_sub_family }) => {
      if (product_family) {
        result[product_family] = productFamilyMap[product_family];
      } else if (product_sub_family) {
        const productFamily = Object.entries(productFamilyMap).find(([, subFamilies]) => subFamilies.includes(product_sub_family))![0] as Product_Family_Name_Enum;

        result[productFamily].push(product_sub_family);
      }
    });

    return result;
  }, [productFamilies]);

  const updateRole = (): void => {
    close(currentRole, currentAdminStatus);
  };

  return (
    <Sheet open={open} onClose={() => close()} width={325} title={t('common:details')}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
          <Avatar
            size={80}
            src={PhotosUtils.getUserAvatarPicture(user)}
            variant="square"
            sx={{
              borderRadius: 6,
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="bodyLarge" sx={{ ...ellipsisStyle }}>
              {user.first_name} {user.last_name}
            </Typography>
            <Typography variant="bodyMedium" color="onSurfaceVariant" sx={{ ...ellipsisStyle }}>
              {user.email}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="titleSmall">
              {t('company:role')}
            </Typography>
            <Typography variant="bodySmall" color="onSurfaceVariant">
              {t('company:role_description')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {availableRoles.map((buyerRole) => (
              <Chip
                key={buyerRole}
                variant="filter"
                label={t(resolvedWording[buyerRole])}
                onClick={() => setCurrentRole(buyerRole)}
                className={currentRole === buyerRole ? 'Kheops-selected' : ''}
              />
            ))}
          </Box>
          <Button
            startIcon={<InfoOutlinedIcon />}
            onClick={() => window.Intercom('showArticle', realm === Business_Profile_Enum.Buyer ? IntercomArticlesIds.BuyerRolesAndPermissions : IntercomArticlesIds.SupplierRolesAndPermissions)}
            sx={{ width: 'fit-content' }}
          >
            {t('common:learn_more_about')}
          </Button>
        </Box>
        <Divider />
        {role === User_Role_Enum.BuyerManager && !!productFamilies.length && (
          <>
            <Box>
              {productFamilies[0].global_setting
                ? (
                  <Chip
                    variant="tag"
                    color="surfaceContainerHigh"
                    label={t(productFamilies[0].global_setting === Global_Product_Family_Enum.All ? 'settings:all_departments' : 'settings:no_departments')}
                  />
                ) : (
                  Object.entries(subFamiliesByFamilies!)
                    .filter(([, subFamilies]) => !!subFamilies.length)
                    .map(([family, subFamilies]) => (
                      <Box key={family} sx={{ p: 1.5 }}>
                        <Typography variant="bodyMedium" sx={{ mb: 1 }}>
                          {t(`product-family:${ProductFamilyTypeIntl[family as Product_Family_Name_Enum]}`)}
                        </Typography>
                        {subFamilies.length === productFamilyMap[family as Product_Family_Name_Enum].length
                          ? (
                            <Chip variant="tag" color="surfaceContainerHigh" label={t('settings:all_departments')} />
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {
                                subFamilies.map((subFamily) => (
                                  <ProductFamilyChip
                                    variant="tag"
                                    subFamily={subFamily}
                                    color="surfaceContainerHighest"
                                    label={t(`product-family:${[PackagingFamilyTypeIntl[subFamily]]}`)}
                                    key={subFamily}
                                  />
                                ))
                              }
                            </Box>
                          )}
                      </Box>
                    ))
                )}
            </Box>
            <Divider />
          </>
        )}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="titleSmall">
              {t('company:admin')}
            </Typography>
            <Switch
              size="large"
              checked={currentAdminStatus}
              onClick={() => setCurrentAdminStatus(!currentAdminStatus)}
            />
          </Box>
          <Typography variant="bodySmall" color="onSurfaceVariant">
            {t('company:admin_description')}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            justifyContent: 'flex-end',
          }}
        >
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="text"
              onClick={() => close()}
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={updateRole}
              disabled={!roleUpdated}
              loading={loading}
              startIcon={<EditIcon />}
            >
              {t('common:update')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Sheet>
  );
}
